import type {
  router as expoRouter,
  Href,
  UnknownInputParams,
} from "expo-router";
import { router as expoRouterInstance } from "expo-router";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { LogoutReason } from "@/constants/Login";
import { ScreenPath } from "@/constants/Navigation";
import {
  filterToAllowedUrlParams,
  RouteParamsStorageKey,
} from "@/constants/WebSessionRouteParams";
import { captureException } from "@/services/AppMonitor";
import store from "@/store";
import { setLogoutReasonAction } from "@/store/reducers/auth";

// expo-router's usePathname() strips group segments (e.g. "(app)/(modals)/weight-history" -> "/weight-history").
// We apply the same normalization on writes so the storage key matches what reads see.
// eslint-disable-next-line sonarjs/slow-regex -- regex is safe: [^)]+ is a negated class and cannot backtrack into \/?
const GroupSegmentPattern = /\([^)]+\)\/?/g;
const normalizePathname = (pathname: string): string => {
  const withoutGroups = pathname.replaceAll(GroupSegmentPattern, "");
  const collapsedSlashes = withoutGroups.replaceAll(/\/+/g, "/");
  const trimmed = collapsedSlashes.replace(/\/$/, "");
  if (isEmpty(trimmed)) return "/";
  if (trimmed.startsWith("/")) return trimmed;
  return `/${trimmed}`;
};

const writeSessionParams = ({
  params,
  pathname,
}: {
  params: UnknownInputParams;
  pathname: string;
}): void => {
  const storage = globalThis.window.sessionStorage;
  if (isNil(storage)) return;

  const normalizedPathname = normalizePathname(pathname);
  const paramsMap = readParamsMap();
  paramsMap[normalizedPathname] = params;
  writeParamsMap({ paramsMap });
};

const writeParamsMap = ({
  paramsMap,
}: {
  paramsMap: Record<string, UnknownInputParams>;
}): void => {
  try {
    const storage = globalThis.window.sessionStorage;
    storage.setItem(RouteParamsStorageKey, JSON.stringify(paramsMap));
  } catch (error) {
    captureException(error, {
      context: {
        extra: {
          message: "Failed to write route params to sessionStorage",
          paramsMapSize: Object.keys(paramsMap).length,
        },
      },
    });
  }
};

const updateSessionParams = (
  params: Record<string, string | undefined>,
  pathname: string = globalThis.window.location.pathname,
  // eslint-disable-next-line max-params
): void => {
  const storage = globalThis.window.sessionStorage;
  if (isNil(storage)) return;

  // absolutely critical to pass in pathname rather than read from window.location,
  // since the race/concurrency of nav state changes may make window.location reads wrong,
  // which can result in writing to one pathname and reading to another and unexpected behavior
  const normalizedPathname = normalizePathname(pathname);
  const paramsMap = readParamsMap();
  const existing = paramsMap[normalizedPathname];

  // a key set to undefined clears it from storage;
  // any other value is upserted;
  // a key not mentioned is left as-is
  const merged = { ...existing, ...params };
  const updated: UnknownInputParams = Object.fromEntries(
    Object.entries(merged).filter(([, value]) => !isNil(value)),
  );

  paramsMap[normalizedPathname] = updated;
  writeParamsMap({ paramsMap });
};

const readParamsMap = (): Record<string, UnknownInputParams> => {
  try {
    const existing = globalThis.window.sessionStorage.getItem(
      RouteParamsStorageKey,
    );
    if (isNil(existing)) return {};
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- JSON.parse returns unknown
    return JSON.parse(existing) as Record<string, UnknownInputParams>;
  } catch (error) {
    // if the JSON.parse errors, we assume the JSON is unrecoverable
    // and proceed here with a fresh/empty paramsMap from scratch to reset things
    captureException(error, {
      context: {
        extra: {
          message: "Failed to parse route params from sessionStorage",
        },
      },
    });
    return {};
  }
};

const readSessionParams = (
  pathname: string,
): undefined | UnknownInputParams => {
  const storage = globalThis.window.sessionStorage;
  if (isNil(storage)) return;

  const normalizedPathname = normalizePathname(pathname);
  return readParamsMap()[normalizedPathname];
};

const clearAllWebSessionRouteParams = (): void => {
  const storage = globalThis.window.sessionStorage;
  if (isNil(storage)) return;
  storage.removeItem(RouteParamsStorageKey);
};

const readUrlProvidedParamOverrides = (): UnknownInputParams => {
  if (isNil(globalThis.window)) return {};

  return filterToAllowedUrlParams(globalThis.window.location.search);
};

type ExpoNavMethod = (typeof expoRouter)["push"];

const isHrefWithParams = (
  href: Href,
): href is { params: UnknownInputParams; pathname: string } => {
  if (typeof href !== "object") return false;
  return !isNil(href.params);
};

const wrapExpoNavMethod = (navMethod: ExpoNavMethod): ExpoNavMethod => {
  // eslint-disable-next-line max-params -- signature must match expo-router's ExpoNavMethod (href, options)
  return (href, options) => {
    const { auth } = store.getState();

    // provide hard guarantee fallback for useHandleAuthExpiration's logic, in case of expo-router edge cases
    if (auth.shouldPreventApiCallsDueToExpiredToken) {
      const target = typeof href === "string" ? href : href.pathname;
      const normalizedTarget = target.startsWith("/") ? target : `/${target}`;
      const loginPath = `/${ScreenPath.Login}`;
      const isLoginRoute =
        normalizedTarget === loginPath ||
        normalizedTarget.startsWith(`${loginPath}/`);
      if (isLoginRoute) return;
      store.dispatch(
        setLogoutReasonAction(LogoutReason.AuthSessionExpiredAtApiCall),
      );
      expoRouterInstance.replace(`/${ScreenPath.Login}`);
      return;
    }

    if (!isHrefWithParams(href)) {
      navMethod(href, options);
      return;
    }
    writeSessionParams({ params: href.params, pathname: href.pathname });
    navMethod({ pathname: href.pathname }, options);
  };
};

export {
  clearAllWebSessionRouteParams,
  readSessionParams,
  readUrlProvidedParamOverrides,
  updateSessionParams,
  wrapExpoNavMethod,
};
